<script setup lang="ts">
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {DashboardWidgetProps, RecentVisit, RecentVisitGroupedByPeriod} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import NamaIconUtils from "@/utils/common/NamaIconUtils.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import RouteNames from "@/router/RouteNames.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";

const props = defineProps<DashboardWidgetProps>();
const widgetId = props.chartLine.widgetId ?? GUIUtilities.RECENT_VISITS_KEY

const visitGroupedByDate = GUIUtilities.fetchRecentVisitsGroupedByDate();

function getIcon(item: RecentVisit) {
  let defaultIcon = 'nm-link'
  if (item.route.name === RouteNames.EDIT_VIEW_ROUTE)
    defaultIcon = "nm-edit"
  else if (item.route.name === RouteNames.LIST_VIEW_ROUTE)
    defaultIcon = "nm-list-vew";
  return NamaIconUtils.toIconCode({
    defaultIcon,
    constant: item.route.query[PlaceTokens.PRM_ENTITY_TYPE] as string
  });
}

function getText(item: RecentVisit) {
  let title = "";
  if (item.title)
    title += item.title
  else {
    let entityType = item.route.query[PlaceTokens.PRM_ENTITY_TYPE] as string;
    const isListRoute = ObjectChecker.areEqual(item.route.name, RouteNames.LIST_VIEW_ROUTE);
    if (isListRoute) {
      title += Translator.translateId("list") + " ";
      entityType += 's'
    }
    title += Translator.translateId(entityType);
  }
  return title;
}

function getVisitsSortedByCount(period: keyof RecentVisitGroupedByPeriod) {
  return visitGroupedByDate[period].sort((a, b) => b.count - a.count)
}

function getDisplayedDate(period: keyof RecentVisitGroupedByPeriod) {
  const visits = visitGroupedByDate[period];
  const date = visits[0].date;
  const today = new Date();
  if (date.getDate() === today.getDate())
    return Translator.translateId('today')!;
  else if (date.getDate() === (today.getDate() - 1))
    return Translator.translateId('yesterday')!;
  else if (period != "olderDays") {
    return DateFieldUtils.dateToString(date);
  } else {
    const fromDate = visits[0].date
    const toDate = visits[visits.length - 1].date
    if (GUIUtilities.isSameDay(fromDate, toDate))
      return DateFieldUtils.dateToString(fromDate)
    let text: string = "";
    text += Translator.translateId("from") + " ";
    text += DateFieldUtils.dateToString(fromDate)
    text += " " + Translator.translateId("to") + " ";
    text += DateFieldUtils.dateToString(toDate)
    return text;
  }
}

function getFormattedCount(count: number) {
  if (count >= 100)
    return "99+";
  else
    return "+" + Math.max(1, Math.floor(count / 5) * 5);
}

function getGroupStyle(period: keyof RecentVisitGroupedByPeriod): Partial<CSSStyleDeclaration> {
  const currentVisitsLength = visitGroupedByDate[period].length;
  if (currentVisitsLength < 4)
    return {flexGrow: '1', maxWidth: 'fit-content'}
  else
    return {flexGrow: currentVisitsLength + ''}
}

function getPeriods(): (keyof RecentVisitGroupedByPeriod)[] {
  return Object.keys(visitGroupedByDate) as (keyof RecentVisitGroupedByPeriod)[];
}

</script>

<template>
  <div :key="chartKey + widgetId"
       class="tw-h-full tw-flex tw-flex-col">
    <div class="tw-px-4 tw-py-3 tw-border-gray-200">
      <div class="tw-flex tw-items-center tw-gap-2">
        <span v-text="Translator.translateId('RecentVisits')"
              class="tw-text-base tw-font-semibold dark:tw-text-gray-200 tw-text-gray-700 tw-capitalize"/>
      </div>
    </div>
    <div class="tw-flex tw-flex-wrap tw-gap-4 tw-p-3 tw-overflow-y-auto">
      <template v-for="period in getPeriods()">
        <div
          v-if="getVisitsSortedByCount(period)?.length>0"
          :style="{...getGroupStyle(period)}"
          class="tw-p-3 dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light tw-w-max tw-rounded-lg nama-deep-shadow">
          <div class="tw-flex tw-flex-wrap tw-items-center tw-border  tw-border-black/10 dark:tw-border-white/20 tw-rounded-md tw-p-1 tw-w-full tw-pt-3 tw-gap-1 tw-relative">
          <span v-text="getDisplayedDate(period)"
                class="tw-absolute tw-left-4 tw-top-0 -tw-translate-y-1/2 tw-text-black tw-text-xs dark:tw-text-white tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark"/>
          <template v-for="visit in getVisitsSortedByCount(period)">
            <div
              :class="{'tw-max-w-fit':visitGroupedByDate[period].length<4}"
              class="tw-p-1 tw-grow tw-flex tw-min-w-40">
              <q-btn
                :to="visit.route"
                flat
                no-caps
                class="tw-rounded-xl tw-min-w-full tw-h-full tw-relative tw-overflow-hidden
                       tw-border tw-border-white/10 dark:tw-border-white/10
                       dark:tw-bg-namaBackground-deep-dark tw-bg-namaBackground-deep-light
                       hover:tw-shadow-lg tw-ring-[1px] tw-ring-transparent dark:hover:tw-ring-white/10 hover:tw-ring-black/10 hover:tw-scale-[1.01]
                       active:tw-scale-[0.98]
                       tw-transition-all tw-duration-300 tw-ease-out
                       tw-py-3 tw-px-6">
                <q-badge
                  floating
                  rounded
                  :label="getFormattedCount(visit.count)"
                  class="tw-absolute tw-top-1 tw-right-1 tw-text-xs tw-font-semibold
                         tw-shadow-md tw-min-w-[24px] tw-h-[24px]
                         tw-border-2 tw-border-namaBackground-bright-light dark:tw-border-namaBackground-bright-dark"
                  color="primary"
                />
                <div class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-w-full">
                  <div class="tw-p-1.5 tw-rounded-lg tw-flex tw-gap-1 tw-bg-primary/10 dark:tw-bg-primary/20
                              tw-transition-colors tw-duration-300">
                    <NamaIcon class="tw-size-6 tw-text-primary"
                              :default-icon="getIcon(visit)"/>
                    <template v-if="visit.route.name === RouteNames.LIST_VIEW_ROUTE">
                      <NamaIcon class="tw-size-6 tw-text-primary"
                                :default-icon="getIcon(visit)"/>
                    </template>
                  </div>
                  <p v-text="getText(visit)"
                     class="dark:tw-text-gray-200 tw-text-gray-700
                            tw-leading-tight tw-normal-case tw-text-sm tw-font-medium
                            tw-text-center tw-line-clamp-2 tw-w-full"/>
                </div>
              </q-btn>
            </div>
          </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
